import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  InputAdornment,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { NavLink } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import SearchIcon from "@/components/Icons/SearchIcon";
import { graphql } from "@/gql";
import { MyFormFragment, OrderByDirection } from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import useDialogState from "@/hooks/useDialogState";
import useSearchText from "@/hooks/useSearchText";
import { colors } from "@/theme/overrides/colors";

import PreviewFormModal from "./components/PreviewFormModal";
import MyForms from "./MyForms/MyForms";
import SentForms from "./SentForms/SentForms";

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: 18,
    fontWeight: 700,
  },
  container: {
    padding: 20,
  },
  tabs: {
    marginTop: 10,
  },
  panels: {
    marginTop: 20,
  },
  mainContentWrapper: {
    paddingLeft: 20,
    paddingRight: 20,
  },
  searchBar: {
    height: 8,
  },
  formButton: {
    marginLeft: 10,
    width: theme.typography.pxToRem(124),
    alignSelf: "center",
  },
}));

const StyledTabs = withStyles(Tabs, {
  indicator: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
    "& > span": {
      maxWidth: 60,
      width: "100%",
      backgroundColor: colors.primary,
    },
  },
});

const StyledTab = withStyles(Tab, {
  root: {
    fontSize: 16,
    fontWeight: 700,
    minWidth: 50,
    textTransform: "capitalize",
    color: "#8EAFA9",
    "&.Mui-selected": {
      color: "#26C9A7",
    },
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      height: 52,
      padding: 0,
      paddingLeft: 16,
      paddingRight: 28,
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const formsPageQuery = graphql(`
  query FormsPage($title: String, $orderByTitle: OrderByDirection) {
    forms(title: $title, orderByTitle: $orderByTitle) {
      nodes {
        ...MyForm
      }
    }
  }
`);

const FormsPage: React.FC = () => {
  const { classes } = useStyles();
  const [value, setValue] = useState<number>(0);
  const [searchValue, setSearchValue] = useSearchText({ debounce: 500 });
  const [order, setOrder] = React.useState<OrderByDirection>(
    OrderByDirection.Asc
  );
  const setDebouncedSortValue = useDebounce(setOrder, 500);

  const handleSort = (event: SelectChangeEvent) => {
    setDebouncedSortValue(event.target.value as OrderByDirection);
  };

  const previewModal = useDialogState();
  const [isSending, setIsSending] = useState<boolean>(false);
  const [currentForm, setCurrentForm] = useState<MyFormFragment | null>(null);
  const { data, loading } = useQuery(formsPageQuery, {
    variables: { title: searchValue, orderByTitle: order },
  });

  const myForms = data?.forms?.nodes ?? [];

  const handleChange = (
    event: React.ChangeEvent<EventTarget | Element>,
    newValue: number
  ) => {
    setValue(newValue);
  };

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.toLowerCase();
    setSearchValue(value);
  }

  return (
    <>
      {previewModal.shown && (
        <PreviewFormModal
          data={currentForm}
          isSending={isSending}
          visibility={previewModal.shown}
          onClose={() => {
            previewModal.hide();
            setIsSending(false);
          }}
        />
      )}
      <Box className={classes.container}>
        <Typography className={classes.title}>Forms</Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <StyledTabs
            className={classes.tabs}
            TabIndicatorProps={{ children: <span /> }}
            value={value}
            onChange={handleChange}
          >
            <StyledTab label="My Forms" />
            <StyledTab label="Sent Forms" />
          </StyledTabs>
          <Box display="flex" alignItems="center">
            {value === 0 && (
              <StyledTextField
                placeholder="Search..."
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon htmlColor={colors.primary} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <Button className={classes.formButton} component={NavLink} to="new">
              Create Form
            </Button>
          </Box>
        </Box>
        <Box className={classes.panels}>
          <TabPanel value={value} index={0}>
            <MyForms
              myForms={myForms}
              loading={loading}
              onPreviewForm={(data, isSending) => {
                setCurrentForm(data);
                setIsSending(isSending);
                previewModal.show();
              }}
              order={order}
              handleSort={handleSort}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <SentForms />
          </TabPanel>
        </Box>
      </Box>
    </>
  );
};

export default FormsPage;
